// @ts-nocheck
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  fetchMyTeamMembers,
  fetchTeamCourseEnrollments,
  fetchTeamModuleProgress,
} from "@/lib/api/teams";
import { useAuth } from "@/hooks/use-auth";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Users,
  GraduationCap,
  TrendingUp,
  CheckCircle,
  Clock,
  BookOpen,
  Award,
  Search,
  MessageCircle,
} from "lucide-react";
import { CardSkeleton } from "@/components/loading";
import { GroupManagementTab } from "@/components/team/GroupManagementTab";

export const Route = createFileRoute("/_authenticated/my-team")({
  beforeLoad: async ({ context }) => {
    if (typeof window === "undefined") return;

    // Reuses the single token validation the parent _authenticated layout already performed for
    // this navigation, rather than issuing a second identical auth server-function call.
    const auth = context.auth;
    if (!auth?.userId) {
      throw redirect({ to: "/login" });
    }

    if (!auth.isSystemAdmin && !auth.isTeamLeader) {
      throw redirect({ to: "/access-denied" });
    }
  },
  component: MyTeamPage,
});

type TeamMember = {
  id: string;
  full_name: string | null;
  email: string;
  status: string;
  created_at: string;
};

type CourseEnrollment = {
  id: string;
  course_id: string;
  student_id: string;
  enrolled_at: string;
  completion_percentage: number;
  status: string;
  completed_at: string | null;
  course: {
    title: string;
    category: string | null;
  };
};

type ModuleProgress = {
  id: string;
  module_id: string;
  student_id: string;
  status: string;
  completed_at: string | null;
  module: {
    title: string;
  };
};

function MyTeamPage() {
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("overview");
  const { user, hasPermission } = useAuth();
  const canManageGroups = hasPermission("manage:my-groups");
  const navigate = useNavigate();

  // Fetch team members
  const { data: teamMembers = [], isLoading: membersLoading } = useQuery<TeamMember[]>({
    queryKey: ["my-team-members", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      return fetchMyTeamMembers(user.id);
    },
    enabled: !!user,
  });

  const studentIdsStr = teamMembers
    .map((m) => m.id)
    .sort()
    .join(",");

  // Fetch course enrollments for team
  const { data: courseEnrollments = [] } = useQuery<CourseEnrollment[]>({
    queryKey: ["team-course-enrollments", studentIdsStr],
    queryFn: async () => {
      const studentIds = teamMembers.map((m) => m.id);
      if (studentIds.length === 0) return [];

      return fetchTeamCourseEnrollments(studentIds);
    },
    enabled: teamMembers.length > 0,
  });

  // Fetch module progress for team
  const { data: moduleProgress = [] } = useQuery<ModuleProgress[]>({
    queryKey: ["team-module-progress", studentIdsStr],
    queryFn: async () => {
      const studentIds = teamMembers.map((m) => m.id);
      if (studentIds.length === 0) return [];

      return fetchTeamModuleProgress(studentIds);
    },
    enabled: teamMembers.length > 0,
  });

  const filteredMembers = teamMembers.filter(
    (m) =>
      !searchQuery ||
      (m.full_name ?? "").toLowerCase().includes(searchQuery.toLowerCase()) ||
      m.email.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  // Calculate stats
  const totalMembers = teamMembers.length;
  const activeStudents = teamMembers.filter((m) => m.status === "approved").length;
  const totalEnrollments = courseEnrollments.length;
  const completedCourses = courseEnrollments.filter((e) => e.status === "completed").length;
  const avgCompletion =
    courseEnrollments.length > 0
      ? courseEnrollments.reduce((acc, e) => acc + e.completion_percentage, 0) /
        courseEnrollments.length
      : 0;

  const getCourseEnrollmentsForStudent = (studentId: string) => {
    return courseEnrollments.filter((e) => e.student_id === studentId);
  };

  const getModuleProgressForStudent = (studentId: string) => {
    return moduleProgress.filter((m) => m.student_id === studentId);
  };

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-8">
      <div>
        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
          <Users className="h-8 w-8" />
          My Team
        </h1>
        <p className="text-muted-foreground mt-2">
          Monitor training progress and support your team members
        </p>
      </div>

      {/* Stats Overview */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Team Members</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalMembers}</div>
            <p className="text-xs text-muted-foreground">{activeStudents} active</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Course Enrollments</CardTitle>
            <GraduationCap className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalEnrollments}</div>
            <p className="text-xs text-muted-foreground">{completedCourses} completed</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Progress</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{avgCompletion.toFixed(0)}%</div>
            <Progress value={avgCompletion} className="mt-2 h-1" />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Completion Rate</CardTitle>
            <Award className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {totalEnrollments > 0 ? ((completedCourses / totalEnrollments) * 100).toFixed(0) : 0}%
            </div>
            <p className="text-xs text-muted-foreground">
              {completedCourses}/{totalEnrollments} courses
            </p>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Team Overview</TabsTrigger>
          <TabsTrigger value="courses">Course Progress</TabsTrigger>
          {canManageGroups && <TabsTrigger value="manage">Manage Groups</TabsTrigger>}
        </TabsList>

        <TabsContent value="overview" className="mt-6 space-y-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search team members..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {membersLoading ? (
            <CardSkeleton count={6} className="md:grid-cols-2 lg:grid-cols-3" />
          ) : filteredMembers.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Users className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
                <p className="text-muted-foreground">
                  {searchQuery
                    ? "No team members found matching your search"
                    : "No team members assigned yet"}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {filteredMembers.map((member) => {
                const enrollments = getCourseEnrollmentsForStudent(member.id);
                const modules = getModuleProgressForStudent(member.id);
                const completedModules = modules.filter((m) => m.status === "completed").length;
                const completedCourses = enrollments.filter((e) => e.status === "completed").length;

                return (
                  <Card key={member.id}>
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div
                            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-bold text-primary-foreground"
                            style={{ background: "var(--gradient-brand)" }}
                          >
                            {(member.full_name ?? member.email).charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <CardTitle className="text-base">
                              {member.full_name || "No name"}
                            </CardTitle>
                            <CardDescription className="text-xs">{member.email}</CardDescription>
                          </div>
                        </div>
                        <div className="flex shrink-0 items-center gap-1.5">
                          <Badge
                            variant={
                              member.status === "approved"
                                ? "default"
                                : member.status === "pending"
                                  ? "secondary"
                                  : "destructive"
                            }
                          >
                            {member.status}
                          </Badge>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            title={`Message ${member.full_name || member.email}`}
                            onClick={() =>
                              navigate({ to: "/messages", search: { dmUserId: member.id } })
                            }
                          >
                            <MessageCircle className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <GraduationCap className="h-4 w-4" />
                          <span>
                            {completedCourses}/{enrollments.length} courses
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <BookOpen className="h-4 w-4" />
                          <span>
                            {completedModules}/{modules.length} modules
                          </span>
                        </div>
                      </div>

                      {enrollments.length > 0 && (
                        <div className="space-y-1">
                          <div className="flex justify-between text-xs text-muted-foreground">
                            <span>Overall Progress</span>
                            <span>
                              {(
                                enrollments.reduce((acc, e) => acc + e.completion_percentage, 0) /
                                enrollments.length
                              ).toFixed(0)}
                              %
                            </span>
                          </div>
                          <Progress
                            value={
                              enrollments.reduce((acc, e) => acc + e.completion_percentage, 0) /
                              enrollments.length
                            }
                            className="h-2"
                          />
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="courses" className="mt-6 space-y-6">
          {filteredMembers.map((member) => {
            const enrollments = getCourseEnrollmentsForStudent(member.id);
            if (enrollments.length === 0) return null;

            return (
              <Card key={member.id}>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <div
                      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold text-primary-foreground"
                      style={{ background: "var(--gradient-brand)" }}
                    >
                      {(member.full_name ?? member.email).charAt(0).toUpperCase()}
                    </div>
                    {member.full_name || member.email}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {enrollments.map((enrollment) => (
                      <div
                        key={enrollment.id}
                        className="flex items-center gap-4 p-3 rounded-lg border"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-medium text-sm truncate">
                              {enrollment.course.title}
                            </p>
                            {enrollment.status === "completed" && (
                              <CheckCircle className="h-4 w-4 text-green-500 shrink-0" />
                            )}
                          </div>
                          {enrollment.course.category && (
                            <Badge variant="outline" className="text-xs mb-2">
                              {enrollment.course.category}
                            </Badge>
                          )}
                          <div className="space-y-1">
                            <div className="flex justify-between text-xs text-muted-foreground">
                              <span>{enrollment.status}</span>
                              <span>{enrollment.completion_percentage}%</span>
                            </div>
                            <Progress value={enrollment.completion_percentage} className="h-1.5" />
                          </div>
                          {enrollment.completed_at && (
                            <p className="text-xs text-muted-foreground mt-1">
                              Completed {new Date(enrollment.completed_at).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            );
          })}

          {filteredMembers.every((m) => getCourseEnrollmentsForStudent(m.id).length === 0) && (
            <Card>
              <CardContent className="py-12 text-center">
                <GraduationCap className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
                <p className="text-muted-foreground">No course enrollments found</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {canManageGroups && (
          <TabsContent value="manage" className="mt-6">
            <GroupManagementTab />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
